import React, { ReactElement, useEffect, useState, useRef } from 'react';
import { Map, YMaps } from 'react-yandex-maps';
import { IPointProps } from "@common/redux/types/map";
import { useYandexTermsLinks } from "@common/hooks/useYandexTermsLinks";
import { ICON_PATHS } from "./constants/constants";
import { useClusterIconLayouts } from "@kursk/components/ui/MapForCatalog/MapComponent/hooks/useClusterIconLayouts";

interface IProp {
  height: number;
  points: IPointProps[];
  mapState?: any;
  handleOnPinClick: (item: IPointProps[]) => void;
  balloonIsOpen: boolean;
  closeBalloon: () => void;
}

export const MapComponent = (
  {
    height,
    points = [],
    mapState,
    handleOnPinClick,
    balloonIsOpen,
    closeBalloon,
  }: IProp
): ReactElement => {
  useYandexTermsLinks();

  const { clusterIconLayout, clusterIconLayoutHovered, clusterIconLayoutActive } = useClusterIconLayouts();

  const objectManagerRef = useRef(null);
  const lastActivePinRef = useRef(null);

  const [ymaps, setYmaps] = useState(null); // это ссылка на yandex-map API
  const [mapRef, setMapRef] = useState(null);

  const setClusterIconLayout = (objectManager, id, iconLayout) => {
    objectManager.clusters.setClusterOptions(id, {
      clusterIconLayout: ymaps.templateLayoutFactory
        .createClass(iconLayout)
    })
  }

  const setObjectIconHref = (objectManager, id, imageHref) => {
    objectManager.objects.setObjectOptions(id, {
      iconImageHref: imageHref
    })
  }

  const clearLastActivePinIcon = () => {
    if (lastActivePinRef.current && objectManagerRef.current) {
      const isCluster = lastActivePinRef.current.type === 'Cluster';
      if (isCluster) {
        setClusterIconLayout(objectManagerRef.current, lastActivePinRef.current.id, clusterIconLayout);
        objectManagerRef.current.clusters.setClusterProperties(lastActivePinRef.current.id, { isActive: false });
      } else {
        setObjectIconHref(objectManagerRef.current, lastActivePinRef.current.id, ICON_PATHS.BALLOON);
        objectManagerRef.current.objects.setObjectProperties(lastActivePinRef.current.id, { isActive: false });
      }

      lastActivePinRef.current = null;
    }
  }

  useEffect(() => {
    if (ymaps && mapRef) {
      if (points.length > 1) {
        mapRef.setBounds(ymaps.util.bounds.fromPoints(points.map(p => p.coords)));
      }
      if (points.length === 1) {
        mapRef.setCenter(points[0].coords);
      }
    }
    if (mapRef && ymaps && points.length > 0) {
      const objectManager = new ymaps.ObjectManager({
        // Чтобы метки начали кластеризоваться, выставляем опцию.
        clusterize: true,
        // ObjectManager принимает те же опции, что и кластеризатор.
        gridSize: 128,
        clusterDisableClickZoom: false,
        showInAlphabeticalOrder: true,
        hideIconOnBalloonOpen: false,
      });

      // Чтобы задать опции одиночным объектам и кластерам,
      // обратимся к дочерним коллекциям ObjectManager.
      objectManager.objects.options.set({
        draggable: false,
        preset: 'islands#greenDotIcon',
        iconImageSize: [44, 44],
        iconImageOffset: [-22, -44],
        iconLayout: 'default#imageWithContent',
        iconImageHref: ICON_PATHS.BALLOON,
        hideIconOnBalloonOpen: false,
        openBalloonOnClick: false,
        hasBalloon: false,
      });

      objectManager.objects.events.add('click', (e) => {
        const objectId = e.get('objectId');
        const obj = objectManager.objects.getById(objectId);

        if (obj.properties.isActive) {
          return;
        }

        setObjectIconHref(objectManager, obj.id, ICON_PATHS.BALLOON_ACTIVE);
        objectManager.objects.setObjectProperties(objectId, { isActive: true });

        clearLastActivePinIcon();
        lastActivePinRef.current = obj;

        setTimeout(() => {
          mapRef.setCenter(obj.geometry.coordinates, mapRef.getZoom(), { duration: 300 });
        }, 50)

        handleOnPinClick([obj]);
      });
      objectManager.objects.events.add('mouseenter', (e) => {
        const objectId = e.get('objectId');
        const obj = objectManager.objects.getById(objectId);

        if (obj && !obj.properties.isActive) {
          setObjectIconHref(objectManager, objectId, ICON_PATHS.BALLOON_HOVERED);
        }
      });
      objectManager.objects.events.add('mouseleave', (e) => {
        const objectId = e.get('objectId');
        const obj = objectManager.objects.getById(objectId);

        if (obj && !obj.properties.isActive) {
          setObjectIconHref(objectManager, objectId, ICON_PATHS.BALLOON);
        }
      });


      objectManager.clusters.options.set({
        clusterIconLayout: ymaps.templateLayoutFactory.createClass(clusterIconLayout),
        clusterIconShape: { type: 'Rectangle', coordinates: [[0, 0], [60, 60]] },
        hasBalloon: false,
        groupByCoordinates: false,
        clusterIconOffset: [-5, -73],
        gridSize: 32,
      });
      objectManager.clusters.events.add('click', (e) => {
        const objectId = e.get('objectId');
        const clusterer = objectManager.clusters.getById(objectId);

        const obj = clusterer.properties.geoObjects;
        const zoom = mapRef.getZoom();

        if (zoom >= 21) {

          if (clusterer.properties.isActive) {
            return;
          }

          clearLastActivePinIcon();
          lastActivePinRef.current = clusterer;

          setClusterIconLayout(objectManager, objectId, clusterIconLayoutActive);
          objectManager.clusters.setClusterProperties(objectId, { isActive: true });

          setTimeout(() => {
            mapRef.setCenter(clusterer.geometry.coordinates, mapRef.getZoom(), { duration: 300 });
          }, 50)

          handleOnPinClick(obj);
        }
      });
      objectManager.clusters.events.add('mouseenter', (e) => {
        const objectId = e.get('objectId');
        const obj = objectManager.clusters.getById(objectId);

        if (obj && !obj.properties.isActive) {
          setClusterIconLayout(objectManager, objectId, clusterIconLayoutHovered);
        }
      });
      objectManager.clusters.events.add('mouseleave', (e) => {
        const objectId = e.get('objectId')
        const obj = objectManager.clusters.getById(objectId);

        if (obj && !obj.properties.isActive) {
          setClusterIconLayout(objectManager, objectId, clusterIconLayout)
        }
      });

      objectManager.add(points.map((point) => {
        return {
          type: 'Feature',
          id: point.id,
          geometry: { type: 'Point', coordinates: point.coords },
          properties: {
            id: point.id,
            entityId: point.entityId,
            pointType: point.type,
          }
        }
      }));

      mapRef.geoObjects.add(objectManager);

      objectManagerRef.current = objectManager;

      return () => {
        objectManager.removeAll();
      }
    }
  }
  , [ymaps, mapRef, points])

  useEffect(() => {
    if (mapRef) {
      // удаление стандартных модулей управления
      // ссылка на документацию яндекса https://yandex.ru/dev/maps/jsapi/doc/2.1/dg/concepts/controls/work.html
      mapRef.controls.remove('geolocationControl');
      mapRef.controls.remove('searchControl');
      mapRef.events.add('boundschange', (event) => {
        if (event.get('newZoom') != event.get('oldZoom')) {
          typeof closeBalloon === 'function' && closeBalloon();
        }
      })
    }
  }, [mapRef]);

  useEffect(() => {
    if (balloonIsOpen === false) {
      clearLastActivePinIcon()
    }
  }, [balloonIsOpen]);

  return (
    <YMaps
      query={{
        load: 'package.full'
      }}
    >
      <Map
        height={height}
        width={'auto'}
        options={{
          suppressMapOpenBlock: true,
        }}
        onLoad={ymap => {
          setYmaps(ymap)
        }}
        defaultState={{ controls: ['zoomControl'], ...mapState }}
        instanceRef={ref => {
          setMapRef(ref)
        }}
        modules={[
          'templateLayoutFactory',
          'clusterer.addon.balloon',
        ]}
      />
    </YMaps>
  );
};
